import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchCharacterBossParses, type ParseMetric } from '../api/characters';
import type { NamedRef, ParseRecordSummary } from '../api/types';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { formatDateTime, formatDecimal, formatDuration, formatNumber } from '../utils/format';
import { getActiveServerSlug, serverCharacterPath, serverGuildPath } from '../utils/siteContext';

function parseRate(parse: ParseRecordSummary, metric: ParseMetric) {
  return metric === 'HEALING' ? parse.hps : parse.dps;
}

function parseAmount(parse: ParseRecordSummary, metric: ParseMetric) {
  return metric === 'HEALING' ? parse.totalHealing : parse.totalDamage;
}

function parsePetAmount(parse: ParseRecordSummary, metric: ParseMetric) {
  return metric === 'HEALING' ? parse.petHealing : parse.petDamage;
}

function parseMetricLabel(metric: ParseMetric) {
  return metric === 'HEALING' ? 'Healing' : 'Damage';
}

function percentileBadgeClass(percentile: number | null | undefined) {
  if (percentile === null || percentile === undefined) return 'percentile-badge percentile-badge--none';
  if (percentile >= 100) return 'percentile-badge percentile-badge--gold';
  if (percentile >= 99) return 'percentile-badge percentile-badge--pink';
  if (percentile >= 95) return 'percentile-badge percentile-badge--orange';
  if (percentile >= 75) return 'percentile-badge percentile-badge--purple';
  if (percentile >= 50) return 'percentile-badge percentile-badge--blue';
  if (percentile >= 25) return 'percentile-badge percentile-badge--green';
  return 'percentile-badge percentile-badge--gray';
}

function percentileLabel(percentile: number | null | undefined) {
  return percentile === null || percentile === undefined ? 'N/A' : formatDecimal(percentile, 0);
}

function partitionKey(partition: NamedRef | null | undefined) {
  return String(partition?.slug ?? partition?.id ?? 'unknown');
}

function partitionLabel(partition: NamedRef | null | undefined) {
  return partition?.name ?? 'No partition';
}

function average(values: number[]) {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function encounterPath(parse: ParseRecordSummary) {
  return `/encounters/${encodeURIComponent(parse.encounterId)}`;
}

export function CharacterBossParsesPage() {
  const { id = '', bossId = '' } = useParams();
  const serverSlug = getActiveServerSlug();
  const [searchParams, setSearchParams] = useSearchParams();
  const metric: ParseMetric = searchParams.get('metric') === 'HEALING' ? 'HEALING' : 'DAMAGE';
  const selectedPartitionKey = searchParams.get('partition') ?? '';
  const query = useQuery({
    queryKey: ['character', id, 'boss', bossId, 'parses', metric],
    queryFn: () => fetchCharacterBossParses(id, bossId, metric),
    enabled: id.length > 0 && bossId.length > 0
  });

  function setMetric(nextMetric: ParseMetric) {
    const next = new URLSearchParams(searchParams);
    if (nextMetric === 'HEALING') next.set('metric', 'HEALING');
    else next.delete('metric');
    setSearchParams(next);
  }

  function setPartition(nextPartitionKey: string) {
    const next = new URLSearchParams(searchParams);
    if (nextPartitionKey) next.set('partition', nextPartitionKey);
    else next.delete('partition');
    setSearchParams(next);
  }

  if (query.isLoading) return <LoadingState label="Loading parse history" />;
  if (query.isError) return <div className="content-wrapper page-main"><ErrorState title="Could not load parse history" error={query.error} /></div>;
  if (!query.data) return null;

  const { character, raidBoss } = query.data;
  const parses = query.data.parses ?? [];
  const partitions = new Map<string, NamedRef | null>();
  for (const parse of parses) {
    const key = partitionKey(parse.rankingPartition);
    if (!partitions.has(key)) partitions.set(key, parse.rankingPartition ?? null);
  }
  const partitionOptions = [...partitions.entries()].map(([key, partition]) => ({ key, partition, label: partitionLabel(partition) }));

  const activeParses = selectedPartitionKey
    ? parses.filter((parse) => partitionKey(parse.rankingPartition) === selectedPartitionKey)
    : parses;

  const bestByPercentile = activeParses.reduce((best, parse) => {
    if (!best) return parse;
    if ((parse.percentile ?? -1) > (best.percentile ?? -1)) return parse;
    if (parse.percentile === best.percentile && parseRate(parse, metric) > parseRate(best, metric)) return parse;
    return best;
  }, activeParses[0] ?? null);
  const bestByRate = activeParses.reduce((best, parse) => (!best || parseRate(parse, metric) > parseRate(best, metric) ? parse : best), activeParses[0] ?? null);
  const avgPercentile = average(activeParses.map((parse) => parse.percentile).filter((value): value is number => value !== null && value !== undefined));
  const avgRate = average(activeParses.map((parse) => parseRate(parse, metric)).filter((value) => Number.isFinite(value)));
  const rateLabel = metric === 'HEALING' ? 'HPS' : 'DPS';
  const amountLabel = metric === 'HEALING' ? 'Healing' : 'Damage';
  const petLabel = metric === 'HEALING' ? 'Pet heal' : 'Pet';

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            {raidBoss?.name ?? 'Unknown Boss'}
            <span className="page-hero__server d-block">{serverSlug}</span>
          </h1>
          <Link className="text-decoration-none text-white" to={serverCharacterPath(serverSlug, character)}><p className="fs-3 mb-0">{character.name}</p></Link>
        </div>
        <div className="page-subnav">
          <div className="content-wrapper">
            <ul className="nav secondary-tabs" aria-label="Character boss parse metric navigation">
              <li className="nav-item">
                <button className={`nav-link ${metric === 'DAMAGE' ? 'active' : ''}`} type="button" onClick={() => setMetric('DAMAGE')}>Damage Parses</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${metric === 'HEALING' ? 'active' : ''}`} type="button" onClick={() => setMetric('HEALING')}>Healing Parses</button>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          <div className="row g-4 align-items-stretch mb-4">
            <div className="col-lg-4">
              <div className="card report-content-card shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="small text-uppercase text-body-secondary fw-semibold mb-2">Encounter History</div>
                  <h2 className="h4 fw-semibold mb-0">{character.name} vs {raidBoss?.name ?? 'Unknown Boss'}</h2>
                </div>
              </div>
            </div>
            <div className="col-lg-8 d-flex align-items-stretch">
              <div className="report-overview-strip w-100">
                <div className="report-overview-item"><small>Parses</small><strong>{formatNumber(activeParses.length)}</strong></div>
                <div className="report-overview-item"><small>Best Parse</small><strong>{bestByPercentile?.percentile === null || bestByPercentile?.percentile === undefined ? 'N/A' : `${formatDecimal(bestByPercentile.percentile, 0)}%`}</strong></div>
                <div className="report-overview-item"><small>Avg Parse</small><strong>{avgPercentile === null ? 'N/A' : `${formatDecimal(avgPercentile, 1)}%`}</strong></div>
                <div className="report-overview-item"><small>Best {rateLabel}</small><strong>{bestByRate ? formatDecimal(parseRate(bestByRate, metric), 1) : '0.0'}</strong></div>
                <div className="report-overview-item"><small>Avg {rateLabel}</small><strong>{avgRate === null ? '0.0' : formatDecimal(avgRate, 1)}</strong></div>
              </div>
            </div>
          </div>

          <div className="card character-parses-card shadow-sm">
            <div className="card-header d-flex flex-wrap align-items-end justify-content-between gap-3 py-3">
              <div className="fw-semibold">Parse History</div>
              <div>
                <label className="form-label small text-uppercase text-body-secondary mb-1" htmlFor="character-boss-partition-select">Partition</label>
                <select
                  id="character-boss-partition-select"
                  className="form-select form-select-sm w-auto"
                  value={selectedPartitionKey}
                  onChange={(event) => setPartition(event.target.value)}
                  disabled={partitionOptions.length === 0}
                >
                  <option value="">All partitions</option>
                  {partitionOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="character-parses-table-wrap">
              <div className="table-responsive">
                <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table character-parses-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Guild</th>
                      <th className="text-end">{rateLabel}</th>
                      <th className="text-end">{amountLabel}</th>
                      <th className="text-end">{petLabel}</th>
                      <th className="text-end">Duration</th>
                      <th>Date</th>
                      <th className="text-end">Parse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeParses.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-body-secondary py-3">No eligible {parseMetricLabel(metric).toLowerCase()} parses for this boss yet.</td>
                      </tr>
                    ) : activeParses.map((parse) => (
                      <tr key={parse.id}>
                        <td className="tabular-nums fw-semibold">
                          {parse.rank && parse.totalParses ? `${formatNumber(parse.rank)} / ${formatNumber(parse.totalParses)}` : 'N/A'}
                        </td>
                        <td>
                          {parse.guild ? (
                            <Link className="link-light text-decoration-none" to={serverGuildPath(serverSlug, parse.guild)}>{parse.guild.name}</Link>
                          ) : <span className="text-body-secondary">No guild</span>}
                        </td>
                        <td className="text-end tabular-nums">
                          <Link className="link-light text-decoration-none" to={encounterPath(parse)}>{formatDecimal(parseRate(parse, metric), 1)}</Link>
                        </td>
                        <td className="text-end tabular-nums">
                          <Link className="link-light text-decoration-none" to={encounterPath(parse)}>{formatNumber(parseAmount(parse, metric))}</Link>
                        </td>
                        <td className="text-end tabular-nums">{formatNumber(parsePetAmount(parse, metric))}</td>
                        <td className="text-end tabular-nums">{formatDuration(parse.durationSec)}</td>
                        <td className="small text-body-secondary text-nowrap">{formatDateTime(parse.encounter.startedAt ?? parse.createdAt)}</td>
                        <td className="text-end">
                          <span className={percentileBadgeClass(parse.percentile)}>{percentileLabel(parse.percentile)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
